import { useCallback } from "react";
import Api from "lib/api/httpRequestHelper";
import UtilsFncs from "utils/UtilsFncs";
import { queryClient } from "lib/queryClient";

const QUERY_OPTIONS = {
  staleTime: 30 * 60 * 1000,
  cacheTime: 30 * 60 * 1000,
};

const isEmptyParam = (v) =>
  v === undefined || v === null || v === "";

const getQueryKey = (scope, params = {}) => {
  const filtered = Object.fromEntries(
    Object.entries(params).filter(([, v]) => !isEmptyParam(v))
  );
  return Object.keys(filtered).length > 0 ? [...scope, filtered] : scope;
};

const wrapResult = (data) => ({ data });
const wrapEmpty = () => ({ data: [] });
const wrapEmptyResult = () => ({ data: null });

const fetchEconomicGroups = async (params = {}) => {
  const requestParams = UtilsFncs.RestParamsComposer(params);
  const response = await Api.get(
    "econnomical-group",
    "commercialstructure",
    requestParams,
    true
  );
  return response?.Result ?? [];
};

const fetchStores = async (params = {}) => {
  const requestParams = UtilsFncs.RestParamsComposer(params);
  const response = await Api.get(
    "store",
    "commercialstructure",
    requestParams,
    true
  );
  return response?.Result ?? [];
};

const fetchChannels = async () => {
  const response = await Api.get(
    "channel",
    "commercialstructure",
    "",
    true
  );
  return response?.Result ?? [];
};

const fetchRepresentatives = async (params = {}) => {
  const requestParams = UtilsFncs.RestParamsComposer(params);
  const response = await Api.get(
    "representative",
    "commercialstructure",
    requestParams,
    true
  );
  return response?.Result ?? [];
};

const fetchBrands = async () => {
  const response = await Api.get("brand", "commercialstructure", "", true);
  return response?.Result ?? [];
};

const fetchStoreGroupSearch = async (params = {}) => {
  const requestParams = UtilsFncs.RestParamsComposer(params);
  const response = await Api.get(
    "store-group-search",
    "commercialstructure",
    requestParams,
    true
  );
  return response?.Result ?? null;
};

const fetchStoreUser = async (params = {}) => {
  const requestParams = UtilsFncs.RestParamsComposer(params);
  const response = await Api.get(
    "store-user",
    "commercialstructure",
    requestParams,
    true
  );
  return response?.Result ?? { ListLojas: [], TotalDePaginas: 0 };
};

const fetchProductLines = async () => {
  const response = await Api.get(
    "products-line-list",
    "product",
    "",
    true
  );
  return response?.Result ?? null;
};

const fetchListCatalog = async (params = { page: 0 }) => {
  const requestParams = UtilsFncs.RestParamsComposer(params);
  const response = await Api.get(
    "list-catalog",
    "trocasDevolucoes",
    requestParams,
    true
  );
  return response?.Result ?? null;
};

const fetchListBillingTypes = async () => {
  const response = await Api.get(
    "list-billing-types",
    "franquias",
    "",
    true
  );
  return response?.Result ?? null;
};

const fetchLogApiScreenList = async () => {
  const response = await Api.get("log-api-screen-list", "log", "", true);
  return response?.Result ?? [];
};

const fetchSalesMode = async (params = { ativo: true }) => {
  const requestParams = UtilsFncs.RestParamsComposer(params);
  const response = await Api.get(
    "sales-mode",
    "storeadditionalinfo",
    requestParams,
    true
  );
  return response?.Result ?? [];
};

const fetchCategoriaList = async () => {
  const response = await Api.get("categoria-list", "conteudo", "", true);
  return response?.Result ?? null;
};

const fetchListSupplierCategories = async () => {
  const response = await Api.get(
    "list-supplier-categories",
    "franquias",
    "",
    true
  );
  return response?.Result ?? null;
};

export const getBrandsData = async () => {
  try {
    return await queryClient.fetchQuery({
      queryKey: ["structure", "brand"],
      queryFn: fetchBrands,
      ...QUERY_OPTIONS,
    });
  } catch (error) {
    console.error("Erro ao buscar marcas:", error);
    throw error;
  }
};

export const preloadStructureData = async () => {
  const fetchers = [
    { queryKey: ["structure", "econnomical-group"], fn: () => fetchEconomicGroups({}) },
    { queryKey: ["structure", "store"], fn: () => fetchStores({}) },
    { queryKey: ["structure", "channel"], fn: fetchChannels },
    { queryKey: ["structure", "representative"], fn: () => fetchRepresentatives({}) },
    { queryKey: ["structure", "brand"], fn: fetchBrands },
  ];
  await Promise.allSettled(
    fetchers.map(({ queryKey, fn }) =>
      queryClient.prefetchQuery({
        queryKey,
        queryFn: fn,
        ...QUERY_OPTIONS,
      })
    )
  );
};

export const clearStructureCache = () => {
  queryClient.removeQueries({ queryKey: ["structure"] });
};

export default function useStructureQueries() {
  const getEconomicGroups = useCallback(async (params = {}) => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: getQueryKey(["structure", "econnomical-group"], params),
        queryFn: () => fetchEconomicGroups(params),
        ...QUERY_OPTIONS,
      });
      return wrapResult(data);
    } catch (error) {
      console.error("Erro ao buscar grupos econômicos:", error);
      return wrapEmpty();
    }
  }, []);

  const getStores = useCallback(async (params = {}) => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: getQueryKey(["structure", "store"], params),
        queryFn: () => fetchStores(params),
        ...QUERY_OPTIONS,
      });
      return wrapResult(data);
    } catch (error) {
      console.error("Erro ao buscar lojas:", error);
      return wrapEmpty();
    }
  }, []);

  const getChannels = useCallback(async () => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["structure", "channel"],
        queryFn: fetchChannels,
        ...QUERY_OPTIONS,
      });
      return wrapResult(data);
    } catch (error) {
      console.error("Erro ao buscar canais:", error);
      return wrapEmpty();
    }
  }, []);

  const getRepresentatives = useCallback(async (params = {}) => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: getQueryKey(["structure", "representative"], params),
        queryFn: () => fetchRepresentatives(params),
        ...QUERY_OPTIONS,
      });
      return wrapResult(data);
    } catch (error) {
      console.error("Erro ao buscar representantes:", error);
      return wrapEmpty();
    }
  }, []);

  const getBrands = useCallback(async () => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["structure", "brand"],
        queryFn: fetchBrands,
        ...QUERY_OPTIONS,
      });
      return wrapResult(data);
    } catch (error) {
      console.error("Erro ao buscar marcas:", error);
      return wrapEmpty();
    }
  }, []);

  const getStoreGroupSearch = useCallback(async (params = {}) => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: getQueryKey(["structure", "store-group-search"], params),
        queryFn: () => fetchStoreGroupSearch(params),
        ...QUERY_OPTIONS,
      });
      return wrapResult(data);
    } catch (error) {
      console.error("Erro ao buscar store-group-search:", error);
      return wrapEmptyResult();
    }
  }, []);

  const getStoreUser = useCallback(async (params = {}) => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: getQueryKey(["structure", "store-user"], params),
        queryFn: () => fetchStoreUser(params),
        ...QUERY_OPTIONS,
      });
      return wrapResult(data);
    } catch (error) {
      console.error("Erro ao buscar store-user:", error);
      return wrapResult({ ListLojas: [], TotalDePaginas: 0 });
    }
  }, []);

  const getProductLines = useCallback(async () => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["structure", "product", "products-line-list"],
        queryFn: fetchProductLines,
        ...QUERY_OPTIONS,
      });
      return wrapResult(data);
    } catch (error) {
      console.error("Erro ao buscar linhas de produtos:", error);
      return wrapEmptyResult();
    }
  }, []);

  const getListCatalog = useCallback(async (params = { page: 0 }) => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: getQueryKey(["structure", "list-catalog"], params),
        queryFn: () => fetchListCatalog(params),
        ...QUERY_OPTIONS,
      });
      return wrapResult(data);
    } catch (error) {
      console.error("Erro ao buscar list-catalog:", error);
      return wrapEmptyResult();
    }
  }, []);

  const getListBillingTypes = useCallback(async () => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["structure", "list-billing-types"],
        queryFn: fetchListBillingTypes,
        ...QUERY_OPTIONS,
      });
      return wrapResult(data);
    } catch (error) {
      console.error("Erro ao buscar list-billing-types:", error);
      return wrapEmptyResult();
    }
  }, []);

  const getLogApiScreenList = useCallback(async () => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["structure", "log-api-screen-list"],
        queryFn: fetchLogApiScreenList,
        ...QUERY_OPTIONS,
      });
      return wrapResult(data);
    } catch (error) {
      console.error("Erro ao buscar log-api-screen-list:", error);
      return wrapEmpty();
    }
  }, []);

  const getSalesMode = useCallback(async (params = { ativo: true }) => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: getQueryKey(["structure", "sales-mode"], params),
        queryFn: () => fetchSalesMode(params),
        ...QUERY_OPTIONS,
      });
      return wrapResult(data);
    } catch (error) {
      console.error("Erro ao buscar sales-mode:", error);
      return wrapEmpty();
    }
  }, []);

  const getCategoriaList = useCallback(async () => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["structure", "categoria-list"],
        queryFn: fetchCategoriaList,
        ...QUERY_OPTIONS,
      });
      return wrapResult(data);
    } catch (error) {
      console.error("Erro ao buscar categoria-list:", error);
      return wrapEmptyResult();
    }
  }, []);

  const getListSupplierCategories = useCallback(async () => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["structure", "list-supplier-categories"],
        queryFn: fetchListSupplierCategories,
        ...QUERY_OPTIONS,
      });
      return wrapResult(data);
    } catch (error) {
      console.error("Erro ao buscar list-supplier-categories:", error);
      return wrapEmptyResult();
    }
  }, []);

  const clearCache = useCallback((endpoint = null, params = {}) => {
    if (endpoint) {
      queryClient.removeQueries({
        queryKey: getQueryKey(["structure", endpoint], params),
      });
    } else {
      queryClient.removeQueries({ queryKey: ["structure"] });
    }
  }, []);

  return {
    getEconomicGroups,
    getStores,
    getChannels,
    getRepresentatives,
    getBrands,
    getStoreGroupSearch,
    getStoreUser,
    getProductLines,
    getListCatalog,
    getListBillingTypes,
    getLogApiScreenList,
    getSalesMode,
    getCategoriaList,
    getListSupplierCategories,
    clearCache,
  };
}
